import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = ((window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) || window.location.origin) + '/api';

const CalculatorDashboard = ({ projectId }) => {
  const [activeCalculator, setActiveCalculator] = useState('wallpaper');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // ---- Numbers the app already knows ----
  // The maths here always worked; the complaint was having to type in figures
  // the app already held. One fetch feeds every calculator.
  const API_BASE = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) + '/api';
  const [projects, setProjects] = useState([]);
  const [pickedProject, setPickedProject] = useState(projectId || '');
  const [projectData, setProjectData] = useState(null);
  const [projectDataLoading, setProjectDataLoading] = useState(false);
  const [prefillNote, setPrefillNote] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/projects`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setProjects(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Could not load projects for the calculators:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE]);

  useEffect(() => {
    if (!pickedProject) { setProjectData(null); return; }
    let cancelled = false;
    (async () => {
      try {
        setProjectDataLoading(true);
        const res = await fetch(`${API_BASE}/calculators/project-data/${pickedProject}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setProjectData(data);
      } catch (err) {
        console.error('Could not load project figures:', err);
        if (!cancelled) setProjectData(null);
      } finally {
        if (!cancelled) setProjectDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE, pickedProject]);

  // Fill whichever calculator is open from a room's recorded dimensions.
  const applyRoomDimensions = (room) => {
    if (!room) return;
    const w = String(room.width_ft || '');
    const d = String(room.depth_ft || room.width_ft || '');
    const h = String(room.height_ft || '');
    const walls = room.wall_count || 4;

    // Walls run width, depth, width, depth around the room.
    setWallpaperData(prev => ({
      ...prev,
      wall_1_width: w, wall_1_height: h,
      wall_2_width: walls >= 2 ? d : '', wall_2_height: walls >= 2 ? h : '',
      wall_3_width: walls >= 3 ? w : '', wall_3_height: walls >= 3 ? h : '',
      wall_4_width: walls >= 4 ? d : '', wall_4_height: walls >= 4 ? h : '',
    }));
    setPaintData(prev => ({ ...prev, room_length: d, room_width: w, wall_height: h }));
    setFlooringData(prev => ({ ...prev, room_length: d, room_width: w }));
    setLightingData(prev => ({ ...prev, room_length: d, room_width: w }));

    setPrefillNote(
      `Filled from ${room.room_name} — ${room.width_ft}ft × ${room.depth_ft}ft, ` +
      `${room.height_ft}ft high, ${walls} wall${walls === 1 ? '' : 's'} ` +
      `(${room.wall_area_sqft} sq ft of wall, ${room.floor_area_sqft} sq ft of floor)`
    );
  };

  // Fill the price field of whichever calculator is open.
  const applyMaterialPrice = (material) => {
    if (!material) return;
    const cost = String(material.cost || '');
    setWallpaperData(prev => ({ ...prev, cost_per_roll: cost, cost_per_yard: cost }));
    setDraperyData(prev => ({ ...prev, cost_per_yard: cost }));
    setHardwareData(prev => ({ ...prev, cost_per_rod: cost }));
    setFlooringData(prev => ({ ...prev, cost_per_sqft: cost }));
    setPrefillNote(
      `Price filled from ${material.name}${material.vendor ? ` (${material.vendor})` : ''} — $${material.cost}`
    );
  };

  // WALLPAPER CALCULATOR STATE - BY 4 INDIVIDUAL WALLS
  const [wallpaperData, setWallpaperData] = useState({
    wallpaper_type: 'double_roll',
    wall_1_width: '',     // Wall 1 width in feet
    wall_1_height: '',    // Wall 1 height in feet
    wall_2_width: '',     // Wall 2 width in feet
    wall_2_height: '',    // Wall 2 height in feet
    wall_3_width: '',     // Wall 3 width in feet
    wall_3_height: '',    // Wall 3 height in feet
    wall_4_width: '',     // Wall 4 width in feet
    wall_4_height: '',    // Wall 4 height in feet
    num_doors: 0,         // Number of standard doors to deduct
    num_windows: 0,       // Number of standard windows to deduct
    pattern_repeat: 0,    // Pattern repeat in inches
    roll_width: 27,       // Standard wallpaper roll width in inches
    roll_length: 33,      // Double roll length in feet (standard is 33ft)
    cost_per_roll: '',    // Cost per double roll
    cost_per_yard: ''     // Cost per yard (for by-yard type)
  });

  // DRAPERY CALCULATOR STATE
  const [draperyData, setDraperyData] = useState({
    window_width: '',
    finished_length: '',
    pleat_type: 'pinch_pleat',
    fullness_ratio: 2.5,
    fabric_width: 54,
    pattern_repeat: null,
    include_lining: false,
    cost_per_yard: ''  // NEW
  });

  // HARDWARE CALCULATOR STATE
  const [hardwareData, setHardwareData] = useState({
    window_width: '',
    rod_overhang_per_side: 6,
    rod_diameter: 1.0,
    drapery_weight: 'medium',
    cost_per_rod: '',  // NEW
    cost_per_bracket: ''  // NEW
  });

  // PAINT CALCULATOR STATE
  const [paintData, setPaintData] = useState({
    room_length: '',
    room_width: '',
    wall_height: '',
    coats: 2,
    coverage_per_gallon: 350
  });

  // FLOORING CALCULATOR STATE
  const [flooringData, setFlooringData] = useState({
    room_length: '',
    room_width: '',
    tile_length: 12,
    tile_width: 12,
    waste_factor: 0.10,
    cost_per_sqft: ''  // NEW
  });

  // LIGHTING CALCULATOR STATE
  const [lightingData, setLightingData] = useState({
    room_length: '',
    room_width: '',
    room_type: 'living_room'
  });

  const calculateWallpaper = async () => {
    // Validate - at least one wall must have dimensions
    const wall1Area = (parseFloat(wallpaperData.wall_1_width) || 0) * (parseFloat(wallpaperData.wall_1_height) || 0);
    const wall2Area = (parseFloat(wallpaperData.wall_2_width) || 0) * (parseFloat(wallpaperData.wall_2_height) || 0);
    const wall3Area = (parseFloat(wallpaperData.wall_3_width) || 0) * (parseFloat(wallpaperData.wall_3_height) || 0);
    const wall4Area = (parseFloat(wallpaperData.wall_4_width) || 0) * (parseFloat(wallpaperData.wall_4_height) || 0);
    
    const totalWallArea = wall1Area + wall2Area + wall3Area + wall4Area;
    
    if (totalWallArea === 0) {
      alert('Please enter dimensions for at least one wall');
      return;
    }
    
    setLoading(true);
    try {
      const rollWidth = parseFloat(wallpaperData.roll_width) || 27; // inches
      const rollLength = parseFloat(wallpaperData.roll_length) || 33; // feet (double roll)
      const patternRepeat = parseFloat(wallpaperData.pattern_repeat) || 0;
      const numDoors = parseInt(wallpaperData.num_doors) || 0;
      const numWindows = parseInt(wallpaperData.num_windows) || 0;
      
      // Deduct for doors (standard door ~21 sq ft = 3ft x 7ft)
      const doorDeduction = numDoors * 21;
      
      // Deduct for windows (standard window ~15 sq ft = 3ft x 5ft)
      const windowDeduction = numWindows * 15;
      
      // Net wall area
      const netWallArea = Math.max(totalWallArea - doorDeduction - windowDeduction, 0);
      
      // Convert roll width from inches to feet
      const rollWidthFeet = rollWidth / 12;
      
      // Usable coverage per double roll (sq ft)
      // Double roll = roll_length ft long x roll_width in wide
      let usableCoveragePerRoll = rollLength * rollWidthFeet;
      
      // Adjust for pattern repeat (reduces usable area by ~15% for patterns)
      if (patternRepeat > 0) {
        const repeatFactor = 0.85; // 15% waste for pattern matching
        usableCoveragePerRoll *= repeatFactor;
      }
      
      // Calculate double rolls needed (round up)
      const doubleRollsNeeded = Math.ceil(netWallArea / usableCoveragePerRoll);
      
      // Calculate yards needed (1 double roll ≈ 11 yards of wallpaper)
      // Formula: roll_length (33ft) / 3 = 11 yards per double roll
      const yardsPerRoll = rollLength / 3;
      const totalYardsNeeded = Math.ceil(doubleRollsNeeded * yardsPerRoll);
      
      // Calculate costs
      let totalCostRolls = null;
      let totalCostYards = null;
      
      if (wallpaperData.cost_per_roll) {
        totalCostRolls = (parseFloat(wallpaperData.cost_per_roll) * doubleRollsNeeded).toFixed(2);
      }
      if (wallpaperData.cost_per_yard) {
        totalCostYards = (parseFloat(wallpaperData.cost_per_yard) * totalYardsNeeded).toFixed(2);
      }
      
      // Build wall details string
      const wallDetails = [];
      if (wall1Area > 0) wallDetails.push(`Wall 1: ${wallpaperData.wall_1_width}'W x ${wallpaperData.wall_1_height}'H = ${wall1Area.toFixed(1)} sq ft`);
      if (wall2Area > 0) wallDetails.push(`Wall 2: ${wallpaperData.wall_2_width}'W x ${wallpaperData.wall_2_height}'H = ${wall2Area.toFixed(1)} sq ft`);
      if (wall3Area > 0) wallDetails.push(`Wall 3: ${wallpaperData.wall_3_width}'W x ${wallpaperData.wall_3_height}'H = ${wall3Area.toFixed(1)} sq ft`);
      if (wall4Area > 0) wallDetails.push(`Wall 4: ${wallpaperData.wall_4_width}'W x ${wallpaperData.wall_4_height}'H = ${wall4Area.toFixed(1)} sq ft`);
      
      const res = {
        data: {
          wall_details: wallDetails.join(' | '),
          total_wall_area: totalWallArea.toFixed(1),
          deductions: `${numDoors} doors, ${numWindows} windows (${(doorDeduction + windowDeduction).toFixed(0)} sq ft)`,
          net_wall_area: netWallArea.toFixed(1),
          coverage_per_roll: usableCoveragePerRoll.toFixed(1),
          double_rolls_needed: doubleRollsNeeded,
          yards_needed: totalYardsNeeded,
          cost_per_roll: wallpaperData.cost_per_roll || 'N/A',
          cost_per_yard: wallpaperData.cost_per_yard || 'N/A',
          total_cost_rolls: totalCostRolls ? `$${totalCostRolls}` : 'Enter cost per roll',
          total_cost_yards: totalCostYards ? `$${totalCostYards}` : 'Enter cost per yard'
        }
      };
      
      setResults(res.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || error.message;
      alert('Error calculating wallpaper: ' + errorMsg);
    }
    setLoading(false);
  };

  const calculateDrapery = async () => {
    // Validate required fields
    if (!draperyData.window_width || !draperyData.finished_length) {
      alert('Please enter Window Width and Finished Length!');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        window_width: parseFloat(draperyData.window_width),
        finished_length: parseFloat(draperyData.finished_length),
        pleat_type: draperyData.pleat_type,
        fullness_ratio: parseFloat(draperyData.fullness_ratio) || 2.5,
        fabric_width: parseFloat(draperyData.fabric_width) || 54,
        pattern_repeat: draperyData.pattern_repeat ? parseFloat(draperyData.pattern_repeat) : null,
        include_lining: draperyData.include_lining
      };
      const res = await axios.post(`${API}/calculators/drapery`, payload);
      
      // Add cost calculation if cost_per_yard is provided
      if (draperyData.cost_per_yard && res.data.fabric_yardage) {
        const costPerYard = parseFloat(draperyData.cost_per_yard);
        const totalCost = costPerYard * res.data.fabric_yardage;
        res.data.cost_per_yard = costPerYard.toFixed(2);
        res.data.total_fabric_cost = totalCost.toFixed(2);
      }
      
      setResults(res.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg).join(', ')
            : JSON.stringify(error.response.data.detail))
        : error.message;
      alert('Error: ' + errorMsg);
    }
    setLoading(false);
  };

  const calculateHardware = async () => {
    // Validate required field
    if (!hardwareData.window_width) {
      alert('Please enter Window Width!');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        window_width: parseFloat(hardwareData.window_width),
        rod_overhang_per_side: parseFloat(hardwareData.rod_overhang_per_side) || 6,
        rod_diameter: parseFloat(hardwareData.rod_diameter) || 1.0,
        drapery_weight: hardwareData.drapery_weight
      };
      const res = await axios.post(`${API}/calculators/hardware`, payload);
      
      // Add cost calculation
      if (hardwareData.cost_per_rod || hardwareData.cost_per_bracket) {
        const rodCost = parseFloat(hardwareData.cost_per_rod) || 0;
        const bracketCost = parseFloat(hardwareData.cost_per_bracket) || 0;
        const totalCost = rodCost + (bracketCost * res.data.brackets_needed);
        res.data.cost_per_rod = rodCost.toFixed(2);
        res.data.cost_per_bracket = bracketCost.toFixed(2);
        res.data.total_hardware_cost = totalCost.toFixed(2);
      }
      
      setResults(res.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail 
        ? (Array.isArray(error.response.data.detail) 
            ? error.response.data.detail.map(e => e.msg).join(', ')
            : JSON.stringify(error.response.data.detail))
        : error.message;
      alert('Error: ' + errorMsg);
    }
    setLoading(false);
  };

  const calculatePaint = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/calculators/paint`, paintData);
      setResults(res.data);
    } catch (error) {
      alert('Error calculating paint: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const calculateFlooring = async () => {
    // Validate required fields
    if (!flooringData.room_length || !flooringData.room_width) {
      alert('Please enter Room Length and Room Width');
      return;
    }
    if (!flooringData.tile_length || !flooringData.tile_width) {
      alert('Please enter Tile Size');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/calculators/flooring`, flooringData);
      
      // Calculate square footage
      const floorSqFt = flooringData.room_length * flooringData.room_width;
      let totalSqFt = floorSqFt;
      
      // If wall height is provided, calculate wall area too
      let wallSqFt = 0;
      if (flooringData.wall_height && flooringData.wall_height > 0) {
        // Wall area = perimeter × height
        const perimeter = 2 * (flooringData.room_length + flooringData.room_width);
        wallSqFt = perimeter * flooringData.wall_height;
        totalSqFt = wallSqFt; // For wall tiling, use wall area
        res.data.calculation_type = 'Wall Tiling';
        res.data.wall_height = `${flooringData.wall_height} ft`;
        res.data.wall_square_footage = wallSqFt.toFixed(2);
        res.data.perimeter = `${perimeter.toFixed(2)} ft`;
      } else {
        res.data.calculation_type = 'Floor Tiling';
        totalSqFt = floorSqFt;
      }
      
      // Apply waste factor
      const wasteFactor = flooringData.waste_factor || 0.10;
      const totalSqFtNeeded = totalSqFt * (1 + wasteFactor);
      
      // Calculate tiles needed
      const tileSqFt = (flooringData.tile_length * flooringData.tile_width) / 144; // Convert sq inches to sq feet
      const tilesNeeded = Math.ceil(totalSqFtNeeded / tileSqFt);
      
      // Add to results
      res.data.tile_size = `${flooringData.tile_length}" x ${flooringData.tile_width}"`;
      res.data.total_sqft_with_waste = totalSqFtNeeded.toFixed(2);
      res.data.tiles_needed = tilesNeeded;
      
      // Add cost calculation if cost_per_sqft is provided
      if (flooringData.cost_per_sqft) {
        const costPerSqft = parseFloat(flooringData.cost_per_sqft);
        const totalCost = costPerSqft * totalSqFtNeeded;
        res.data.cost_per_sqft = `$${costPerSqft.toFixed(2)}`;
        res.data.total_flooring_cost = `$${totalCost.toFixed(2)}`;
      }
      
      setResults(res.data);
    } catch (error) {
      alert('Error calculating flooring: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const calculateLighting = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/calculators/lighting`, lightingData);
      setResults(res.data);
    } catch (error) {
      alert('Error calculating lighting: ' + (error.response?.data?.detail || error.message));
    }
    setLoading(false);
  };

  const calculators = [
    { id: 'wallpaper', name: '📐 Wallpaper', icon: '🎨' },
    { id: 'drapery', name: '🪟 Drapery', icon: '🪟' },
    { id: 'hardware', name: '🔧 Hardware', icon: '🔧' },
    { id: 'paint', name: '🎨 Paint', icon: '🖌️' },
    { id: 'flooring', name: '⬜ Tile & Flooring', icon: '⬜' },
    { id: 'lighting', name: '💡 Lighting', icon: '💡' },
    { id: 'sqft', name: '📏 Square Ft', icon: '📐' },
    { id: 'convert', name: '🔄 Convert', icon: '🔄' }
  ];

  return (
    <div className="calculator-dashboard" style={styles.dashboard}>
      <div style={styles.header}>
        <h2 style={styles.title}>🧮 Professional Calculators</h2>
        <p style={styles.subtitle}>Industry-standard calculations for all your design needs</p>
      </div>

      {/* Pull in figures the app already holds, instead of retyping them */}
      <div style={{
        border: '1px solid #525252', borderRadius: '8px', padding: '14px 16px',
        marginBottom: '18px', backgroundColor: '#1c1c1c'
      }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#d4af37', fontSize: '13px', fontWeight: 600 }}>
            Use figures from a project
          </span>

          <select
            data-testid="calc-project"
            value={pickedProject}
            onChange={(e) => { setPickedProject(e.target.value); setPrefillNote(''); }}
            style={{
              padding: '7px 10px', backgroundColor: '#0f0f0f', color: '#d4d4d4',
              border: '1px solid #525252', borderRadius: '6px', minWidth: '180px'
            }}
          >
            <option value="">Choose a project…</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <select
            data-testid="calc-room"
            disabled={!projectData || (projectData.rooms || []).length === 0}
            defaultValue=""
            onChange={(e) => {
              const room = (projectData?.rooms || []).find(r => r.room_name === e.target.value);
              applyRoomDimensions(room);
            }}
            style={{
              padding: '7px 10px', backgroundColor: '#0f0f0f', color: '#d4d4d4',
              border: '1px solid #525252', borderRadius: '6px', minWidth: '190px'
            }}
          >
            <option value="">
              {projectDataLoading
                ? 'Loading…'
                : (projectData?.rooms || []).length
                  ? 'Room dimensions…'
                  : 'No measured rooms'}
            </option>
            {(projectData?.rooms || []).map(r => (
              <option key={r.room_name} value={r.room_name}>
                {r.room_name} — {r.width_ft}×{r.depth_ft}×{r.height_ft}ft
              </option>
            ))}
          </select>

          <select
            data-testid="calc-material"
            disabled={!projectData || (projectData.materials || []).length === 0}
            defaultValue=""
            onChange={(e) => {
              const m = (projectData?.materials || []).find(x => x.name === e.target.value);
              applyMaterialPrice(m);
            }}
            style={{
              padding: '7px 10px', backgroundColor: '#0f0f0f', color: '#d4d4d4',
              border: '1px solid #525252', borderRadius: '6px', minWidth: '190px'
            }}
          >
            <option value="">
              {(projectData?.materials || []).length ? 'Material price…' : 'No priced materials'}
            </option>
            {(projectData?.materials || []).map(m => (
              <option key={`${m.name}|${m.vendor}`} value={m.name}>
                {m.name} — ${m.cost}{m.vendor ? ` · ${m.vendor}` : ''}
              </option>
            ))}
          </select>
        </div>

        {prefillNote && (
          <div data-testid="calc-prefill-note" style={{ color: '#7FBF9B', fontSize: '12px', marginTop: '10px' }}>
            ✓ {prefillNote}
          </div>
        )}

        {projectData && (projectData.rooms || []).length === 0 && !projectDataLoading && (
          <div style={{ color: '#a3a3a3', fontSize: '12px', marginTop: '10px' }}>
            No room in this project has measurements yet. Dimensions come from a room&rsquo;s
            Room Finishes schedule — build one and it will appear here.
          </div>
        )}
      </div>

      {/* Calculator Selector */}
      <div style={styles.calculatorSelector}>
        {calculators.map(calc => (
          <button
            key={calc.id}
            onClick={() => {
              setActiveCalculator(calc.id);
              setResults(null);
            }}
            style={{
              ...styles.calcButton,
              ...(activeCalculator === calc.id ? styles.calcButtonActive : {})
            }}
          >
            <span style={styles.calcIcon}>{calc.icon}</span>
            <span>{calc.name}</span>
          </button>
        ))}
      </div>

      {/* Calculator Forms */}
      <div style={styles.calculatorContent}>
        {/* WALLPAPER CALCULATOR - BY 4 INDIVIDUAL WALLS */}
        {activeCalculator === 'wallpaper' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>📐 Wallpaper Calculator</h3>
            <p style={styles.formSubtitle}>Enter dimensions for each wall - Double Rolls & Yards needed</p>
            
            {/* WALL 1 */}
            <div style={{...styles.formGroup, marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#E67E22'}}>🧱 Wall 1</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Width (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_1_width}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_1_width: e.target.value})}
                  style={styles.input}
                  placeholder="12"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Height (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_1_height}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_1_height: e.target.value})}
                  style={styles.input}
                  placeholder="9"
                />
              </div>
            </div>
            
            {/* WALL 2 */}
            <div style={{...styles.formGroup, marginTop: '12px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#3498DB'}}>🧱 Wall 2</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Width (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_2_width}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_2_width: e.target.value})}
                  style={styles.input}
                  placeholder="15"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Height (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_2_height}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_2_height: e.target.value})}
                  style={styles.input}
                  placeholder="9"
                />
              </div>
            </div>
            
            {/* WALL 3 */}
            <div style={{...styles.formGroup, marginTop: '12px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#27AE60'}}>🧱 Wall 3</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Width (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_3_width}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_3_width: e.target.value})}
                  style={styles.input}
                  placeholder="12"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Height (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_3_height}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_3_height: e.target.value})}
                  style={styles.input}
                  placeholder="9"
                />
              </div>
            </div>
            
            {/* WALL 4 */}
            <div style={{...styles.formGroup, marginTop: '12px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#9B59B6'}}>🧱 Wall 4</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Width (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_4_width}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_4_width: e.target.value})}
                  style={styles.input}
                  placeholder="15"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Height (feet)</label>
                <input
                  type="number"
                  value={wallpaperData.wall_4_height}
                  onChange={(e) => setWallpaperData({...wallpaperData, wall_4_height: e.target.value})}
                  style={styles.input}
                  placeholder="9"
                />
              </div>
            </div>

            {/* DEDUCTIONS */}
            <div style={{...styles.formGroup, marginTop: '16px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#d4af37'}}>🚪 Deductions</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}># of Doors</label>
                <input
                  type="number"
                  value={wallpaperData.num_doors}
                  onChange={(e) => setWallpaperData({...wallpaperData, num_doors: parseInt(e.target.value) || 0})}
                  style={styles.input}
                  placeholder="2"
                  min="0"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}># of Windows</label>
                <input
                  type="number"
                  value={wallpaperData.num_windows}
                  onChange={(e) => setWallpaperData({...wallpaperData, num_windows: parseInt(e.target.value) || 0})}
                  style={styles.input}
                  placeholder="3"
                  min="0"
                />
              </div>
            </div>

            {/* WALLPAPER SPECS */}
            <div style={{...styles.formGroup, marginTop: '16px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#d4af37'}}>🎨 Wallpaper Specs</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Roll Width (inches)</label>
                <input
                  type="number"
                  value={wallpaperData.roll_width}
                  onChange={(e) => setWallpaperData({...wallpaperData, roll_width: parseFloat(e.target.value)})}
                  style={styles.input}
                  placeholder="27"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pattern Repeat (inches)</label>
                <input
                  type="number"
                  value={wallpaperData.pattern_repeat}
                  onChange={(e) => setWallpaperData({...wallpaperData, pattern_repeat: parseFloat(e.target.value) || 0})}
                  style={styles.input}
                  placeholder="0 (no pattern)"
                />
              </div>
            </div>

            {/* COST */}
            <div style={{...styles.formGroup, marginTop: '16px', marginBottom: '8px'}}>
              <label style={{...styles.label, fontWeight: 'bold', color: '#d4af37'}}>💰 Pricing (Optional)</label>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cost Per Double Roll ($)</label>
                <input
                  type="number"
                  value={wallpaperData.cost_per_roll}
                  onChange={(e) => setWallpaperData({...wallpaperData, cost_per_roll: e.target.value})}
                  style={styles.input}
                  placeholder="125.00"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cost Per Yard ($)</label>
                <input
                  type="number"
                  value={wallpaperData.cost_per_yard}
                  onChange={(e) => setWallpaperData({...wallpaperData, cost_per_yard: e.target.value})}
                  style={styles.input}
                  placeholder="45.00"
                />
              </div>
            </div>

            <button onClick={calculateWallpaper} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Wallpaper'}
            </button>
          </div>
        )}

        {/* DRAPERY CALCULATOR */}
        {activeCalculator === 'drapery' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>🪟 Drapery Fabric Calculator</h3>
            <p style={styles.formSubtitle}>Calculate yardage for all pleat types with fullness ratios</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Window Width (inches)</label>
                <input
                  type="number"
                  value={draperyData.window_width}
                  onChange={(e) => setDraperyData({...draperyData, window_width: e.target.value})}
                  style={styles.input}
                  placeholder="80"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Finished Length (inches)</label>
                <input
                  type="number"
                  value={draperyData.finished_length}
                  onChange={(e) => setDraperyData({...draperyData, finished_length: e.target.value})}
                  style={styles.input}
                  placeholder="84"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Pleat Type</label>
                <select
                  value={draperyData.pleat_type}
                  onChange={(e) => setDraperyData({...draperyData, pleat_type: e.target.value})}
                  style={styles.select}
                >
                  <option value="pinch_pleat">Pinch Pleat</option>
                  <option value="goblet">Goblet</option>
                  <option value="grommet">Grommet</option>
                  <option value="rod_pocket">Rod Pocket</option>
                  <option value="ripplefold">Ripplefold</option>
                  <option value="box_pleat">Box Pleat</option>
                  <option value="euro_pleat">Euro Pleat</option>
                  <option value="tab_top">Tab Top</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fullness Ratio</label>
                <select
                  value={draperyData.fullness_ratio}
                  onChange={(e) => setDraperyData({...draperyData, fullness_ratio: parseFloat(e.target.value)})}
                  style={styles.select}
                >
                  <option value="1.5">1.5x</option>
                  <option value="2.0">2.0x</option>
                  <option value="2.5">2.5x</option>
                  <option value="3.0">3.0x</option>
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={draperyData.include_lining}
                  onChange={(e) => setDraperyData({...draperyData, include_lining: e.target.checked})}
                  style={styles.checkbox}
                />
                Include Lining Calculation
              </label>
            </div>

            <button onClick={calculateDrapery} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Drapery'}
            </button>
          </div>
        )}

        {/* HARDWARE CALCULATOR */}
        {activeCalculator === 'hardware' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>🔧 Drapery Hardware Calculator</h3>
            <p style={styles.formSubtitle}>Calculate rod width and bracket requirements (60-240")</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Window Width (inches)</label>
                <input
                  type="number"
                  value={hardwareData.window_width}
                  onChange={(e) => setHardwareData({...hardwareData, window_width: e.target.value})}
                  style={styles.input}
                  placeholder="60-240"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rod Overhang Per Side (inches)</label>
                <input
                  type="number"
                  value={hardwareData.rod_overhang_per_side}
                  onChange={(e) => setHardwareData({...hardwareData, rod_overhang_per_side: e.target.value})}
                  style={styles.input}
                  placeholder="6"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rod Diameter (inches)</label>
                <select
                  value={hardwareData.rod_diameter}
                  onChange={(e) => setHardwareData({...hardwareData, rod_diameter: parseFloat(e.target.value)})}
                  style={styles.select}
                >
                  <option value="0.5">1/2"</option>
                  <option value="0.75">3/4"</option>
                  <option value="1.0">1"</option>
                  <option value="1.25">1 1/4"</option>
                  <option value="1.5">1 1/2"</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Drapery Weight</label>
                <select
                  value={hardwareData.drapery_weight}
                  onChange={(e) => setHardwareData({...hardwareData, drapery_weight: e.target.value})}
                  style={styles.select}
                >
                  <option value="light">Light</option>
                  <option value="medium">Medium</option>
                  <option value="heavy">Heavy</option>
                </select>
              </div>
            </div>

            <button onClick={calculateHardware} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Hardware'}
            </button>
          </div>
        )}

        {/* PAINT CALCULATOR */}
        {activeCalculator === 'paint' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>🎨 Paint Calculator</h3>
            <p style={styles.formSubtitle}>Calculate gallons needed for your room</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Length (feet)</label>
                <input
                  type="number"
                  value={paintData.room_length}
                  onChange={(e) => setPaintData({...paintData, room_length: parseFloat(e.target.value)})}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Width (feet)</label>
                <input
                  type="number"
                  value={paintData.room_width}
                  onChange={(e) => setPaintData({...paintData, room_width: parseFloat(e.target.value)})}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Wall Height (feet)</label>
                <input
                  type="number"
                  value={paintData.wall_height}
                  onChange={(e) => setPaintData({...paintData, wall_height: parseFloat(e.target.value)})}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Number of Coats</label>
                <input
                  type="number"
                  value={paintData.coats}
                  onChange={(e) => setPaintData({...paintData, coats: parseInt(e.target.value)})}
                  style={styles.input}
                />
              </div>
            </div>

            <button onClick={calculatePaint} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Paint'}
            </button>
          </div>
        )}

        {/* FLOORING CALCULATOR */}
        {activeCalculator === 'flooring' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>⬜ Tile/Flooring Calculator</h3>
            <p style={styles.formSubtitle}>Calculate tiles needed for floors or walls with waste factor and total cost</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Length (feet)</label>
                <input
                  type="number"
                  value={flooringData.room_length}
                  onChange={(e) => setFlooringData({...flooringData, room_length: parseFloat(e.target.value)})}
                  style={styles.input}
                  placeholder="12"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Width (feet)</label>
                <input
                  type="number"
                  value={flooringData.room_width}
                  onChange={(e) => setFlooringData({...flooringData, room_width: parseFloat(e.target.value)})}
                  style={styles.input}
                  placeholder="10"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Wall Height (feet) <span style={{color: '#888', fontSize: '11px'}}>- for walls only</span></label>
                <input
                  type="number"
                  value={flooringData.wall_height || ''}
                  onChange={(e) => setFlooringData({...flooringData, wall_height: e.target.value ? parseFloat(e.target.value) : null})}
                  style={styles.input}
                  placeholder="8 (optional)"
                />
              </div>
            </div>

            {/* Tile Size - Preset or Custom */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Tile Size Preset</label>
              <select
                value={`${flooringData.tile_length}x${flooringData.tile_width}`}
                onChange={(e) => {
                  if (e.target.value === 'custom') return;
                  const [l, w] = e.target.value.split('x').map(Number);
                  setFlooringData({...flooringData, tile_length: l, tile_width: w});
                }}
                style={styles.select}
              >
                <option value="4x4">4" x 4"</option>
                <option value="6x6">6" x 6"</option>
                <option value="8x8">8" x 8"</option>
                <option value="12x12">12" x 12"</option>
                <option value="12x24">12" x 24"</option>
                <option value="18x18">18" x 18"</option>
                <option value="24x24">24" x 24"</option>
                <option value="6x36">6" x 36" (Plank)</option>
                <option value="6x48">6" x 48" (Long Plank)</option>
                <option value="custom">Custom Size (enter below)</option>
              </select>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Tile Length (inches)</label>
                <input
                  type="number"
                  value={flooringData.tile_length}
                  onChange={(e) => setFlooringData({...flooringData, tile_length: parseFloat(e.target.value)})}
                  style={styles.input}
                  placeholder="12"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Tile Width (inches)</label>
                <input
                  type="number"
                  value={flooringData.tile_width}
                  onChange={(e) => setFlooringData({...flooringData, tile_width: parseFloat(e.target.value)})}
                  style={styles.input}
                  placeholder="12"
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Waste Factor (%)</label>
                <input
                  type="number"
                  value={flooringData.waste_factor * 100}
                  onChange={(e) => setFlooringData({...flooringData, waste_factor: parseFloat(e.target.value) / 100})}
                  style={styles.input}
                  placeholder="10"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Cost Per Sq Ft ($)</label>
                <input
                  type="number"
                  value={flooringData.cost_per_sqft}
                  onChange={(e) => setFlooringData({...flooringData, cost_per_sqft: e.target.value})}
                  style={styles.input}
                  placeholder="5.99"
                  step="0.01"
                />
              </div>
            </div>

            <button onClick={calculateFlooring} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Flooring'}
            </button>
          </div>
        )}

        {/* LIGHTING CALCULATOR */}
        {activeCalculator === 'lighting' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>💡 Lighting Calculator</h3>
            <p style={styles.formSubtitle}>Calculate lumens needed based on room type</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Length (feet)</label>
                <input
                  type="number"
                  value={lightingData.room_length}
                  onChange={(e) => setLightingData({...lightingData, room_length: parseFloat(e.target.value)})}
                  style={styles.input}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Room Width (feet)</label>
                <input
                  type="number"
                  value={lightingData.room_width}
                  onChange={(e) => setLightingData({...lightingData, room_width: parseFloat(e.target.value)})}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Room Type</label>
              <select
                value={lightingData.room_type}
                onChange={(e) => setLightingData({...lightingData, room_type: e.target.value})}
                style={styles.select}
              >
                <option value="living_room">Living Room</option>
                <option value="kitchen">Kitchen</option>
                <option value="bedroom">Bedroom</option>
                <option value="bathroom">Bathroom</option>
                <option value="dining_room">Dining Room</option>
                <option value="office">Office</option>
              </select>
            </div>

            <button onClick={calculateLighting} disabled={loading} style={styles.calculateButton}>
              {loading ? '⏳ Calculating...' : '🧮 Calculate Lighting'}
            </button>
          </div>
        )}
      </div>



        {/* SQUARE FOOTAGE CALCULATOR */}
        {activeCalculator === 'sqft' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>📏 Square Footage Calculator</h3>
            <p style={styles.formSubtitle}>Calculate total square footage for rooms</p>
            
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Length (feet)</label>
                <input
                  type="number"
                  placeholder="Enter length"
                  style={styles.input}
                  id="sqft-length"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label style={styles.label}>Width (feet)</label>
                <input
                  type="number"
                  placeholder="Enter width"
                  style={styles.input}
                  id="sqft-width"
                />
              </div>
            </div>

            <button 
              onClick={async () => {
                setLoading(true);
                try {
                  const length = parseFloat(document.getElementById('sqft-length').value);
                  const width = parseFloat(document.getElementById('sqft-width').value);
                  const res = await axios.post(`${API}/calculators/square-footage`, { length, width });
                  setResults(res.data);
                } catch (error) {
                  alert('Error: ' + (error.response?.data?.detail || error.message));
                }
                setLoading(false);
              }}
              disabled={loading}
              style={styles.calculateButton}
            >
              {loading ? 'Calculating...' : 'Calculate Square Footage'}
            </button>
          </div>
        )}

        {/* CONVERT CALCULATOR */}
        {activeCalculator === 'convert' && (
          <div style={styles.form}>
            <h3 style={styles.formTitle}>🔄 Unit Converter</h3>
            <p style={styles.formSubtitle}>Convert between common design measurements</p>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Conversion Type</label>
              <select
                style={styles.input}
                id="convert-type"
                onChange={(e) => {
                  document.getElementById('convert-value').value = '';
                  setResults(null);
                }}
              >
                <option value="in-to-ft">Inches → Feet</option>
                <option value="ft-to-in">Feet → Inches</option>
                <option value="ft-to-m">Feet → Meters</option>
                <option value="m-to-ft">Meters → Feet</option>
                <option value="sqft-to-sqm">Square Feet → Square Meters</option>
                <option value="sqm-to-sqft">Square Meters → Square Feet</option>
                <option value="yd-to-m">Yards → Meters</option>
                <option value="m-to-yd">Meters → Yards</option>
                <option value="cm-to-in">Centimeters → Inches</option>
                <option value="in-to-cm">Inches → Centimeters</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Value to Convert</label>
              <input
                type="number"
                placeholder="Enter value"
                style={styles.input}
                id="convert-value"
                step="0.01"
              />
            </div>

            <button 
              onClick={() => {
                const type = document.getElementById('convert-type').value;
                const value = parseFloat(document.getElementById('convert-value').value);
                
                if (isNaN(value)) {
                  alert('Please enter a valid number');
                  return;
                }
                
                let result, fromUnit, toUnit;
                
                switch(type) {
                  case 'in-to-ft':
                    result = value / 12;
                    fromUnit = 'inches';
                    toUnit = 'feet';
                    break;
                  case 'ft-to-in':
                    result = value * 12;
                    fromUnit = 'feet';
                    toUnit = 'inches';
                    break;
                  case 'ft-to-m':
                    result = value * 0.3048;
                    fromUnit = 'feet';
                    toUnit = 'meters';
                    break;
                  case 'm-to-ft':
                    result = value / 0.3048;
                    fromUnit = 'meters';
                    toUnit = 'feet';
                    break;
                  case 'sqft-to-sqm':
                    result = value * 0.0929;
                    fromUnit = 'sq ft';
                    toUnit = 'sq meters';
                    break;
                  case 'sqm-to-sqft':
                    result = value / 0.0929;
                    fromUnit = 'sq meters';
                    toUnit = 'sq ft';
                    break;
                  case 'yd-to-m':
                    result = value * 0.9144;
                    fromUnit = 'yards';
                    toUnit = 'meters';
                    break;
                  case 'm-to-yd':
                    result = value / 0.9144;
                    fromUnit = 'meters';
                    toUnit = 'yards';
                    break;
                  case 'cm-to-in':
                    result = value / 2.54;
                    fromUnit = 'cm';
                    toUnit = 'inches';
                    break;
                  case 'in-to-cm':
                    result = value * 2.54;
                    fromUnit = 'inches';
                    toUnit = 'cm';
                    break;
                  default:
                    result = value;
                    fromUnit = '';
                    toUnit = '';
                }
                
                setResults({
                  'Original Value': `${value} ${fromUnit}`,
                  'Converted Value': `${result.toFixed(4)} ${toUnit}`,
                  'Conversion': `${fromUnit} → ${toUnit}`
                });
              }}
              disabled={loading}
              style={styles.calculateButton}
            >
              Convert
            </button>
          </div>
        )}

      {/* RESULTS DISPLAY */}
      {results && (
        <div style={styles.results}>
          <h3 style={styles.resultsTitle}>📊 Calculation Results</h3>
          <div style={styles.resultsContent}>
            {Object.entries(results).map(([key, value]) => (
              <div key={key} style={styles.resultRow}>
                <span style={styles.resultLabel}>{key.replace(/_/g, ' ').toUpperCase()}:</span>
                <span style={styles.resultValue}>
                  {typeof value === 'number' ? value.toFixed(2) : value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  dashboard: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#0a0a0a',
    minHeight: '100vh'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
    padding: '30px 20px',
    background: 'linear-gradient(135deg, #4a4a4a 0%, #3a3a3a 30%, #2a2a2a 70%, #1a1a1a 100%)',
    borderRadius: '10px',
    border: '1px solid #8b7355',
    boxShadow: '0 0 20px rgba(139, 115, 85, 0.3), inset 0 0 40px rgba(255, 255, 255, 0.12), inset 0 0 70px rgba(0, 0, 0, 0.4)'
  },
  title: {
    fontSize: '32px',
    fontWeight: '300',
    color: '#D4C5A9',
    margin: '0 0 10px 0',
    letterSpacing: '2px'
  },
  subtitle: {
    fontSize: '16px',
    color: '#D4C5A9',
    margin: 0
  },
  calculatorSelector: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '15px',
    marginBottom: '30px'
  },
  calcButton: {
    padding: '15px 10px',
    border: '1px solid #8b7355',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 50%, #2a2a2a 100%)',
    color: '#D4C5A9',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '5px',
    boxShadow: '0 4px 15px rgba(139, 115, 85, 0.2)'
  },
  calcButtonActive: {
    background: 'linear-gradient(135deg, #8b7355 0%, #a0845c 50%, #8b7355 100%)',
    borderColor: '#a0845c',
    color: '#1a1a1a',
    transform: 'scale(1.05)',
    boxShadow: '0 4px 15px rgba(139, 115, 85, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
  },
  calcIcon: {
    fontSize: '24px'
  },
  calculatorContent: {
    background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 30%, rgba(0,0,0,0.95) 100%)',
    borderRadius: '10px',
    padding: '30px',
    marginBottom: '20px',
    border: '1px solid rgba(139, 115, 85, 0.3)'
  },
  form: {
    maxWidth: '800px',
    margin: '0 auto'
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: '300',
    color: '#D4A574',
    marginBottom: '10px',
    letterSpacing: '1px'
  },
  formSubtitle: {
    fontSize: '14px',
    color: '#D4C5A9',
    marginBottom: '30px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#D4C5A9',
    fontSize: '14px',
    fontWeight: '500'
  },
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #8b7355',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#D4C5A9',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  select: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #8b7355',
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#D4C5A9',
    fontSize: '16px',
    boxSizing: 'border-box'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    color: '#D4C5A9',
    fontSize: '14px',
    cursor: 'pointer'
  },
  checkbox: {
    marginRight: '10px',
    width: '20px',
    height: '20px',
    cursor: 'pointer'
  },
  calculateButton: {
    width: '100%',
    padding: '15px',
    borderRadius: '10px',
    border: 'none',
    background: 'linear-gradient(135deg, #8b7355 0%, #a0845c 50%, #8b7355 100%)',
    color: '#1a1a1a',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    marginTop: '20px',
    boxShadow: '0 4px 15px rgba(139, 115, 85, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
  },
  results: {
    background: 'linear-gradient(135deg, rgba(0,0,0,0.95) 0%, rgba(30,30,30,0.9) 30%, rgba(0,0,0,0.95) 100%)',
    borderRadius: '10px',
    padding: '30px',
    border: '2px solid #8b7355'
  },
  resultsTitle: {
    fontSize: '24px',
    fontWeight: '300',
    color: '#D4A574',
    marginBottom: '20px',
    letterSpacing: '1px'
  },
  resultsContent: {
    display: 'grid',
    gap: '15px'
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '15px',
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: '8px',
    borderLeft: '4px solid #8b7355'
  },
  resultLabel: {
    color: '#D4C5A9',
    fontSize: '14px',
    fontWeight: '500'
  },
  resultValue: {
    color: '#D4A574',
    fontSize: '18px',
    fontWeight: 'bold'
  }
};

export default CalculatorDashboard;
